import tiersCollection from "./tiers.js";
import customersCollection from "./customers.js";
import transactionsCollection from "./transactions.js";
import tierChangesCollection from "./tier_changes.js";
import walletPassesCollection from "./wallet_passes.js";
import walletSettingsCollection from "./wallet_settings.js";
import walletDevicesCollection from "./wallet_devices.js";
import syncLogsCollection from "./sync_logs.js";
import usersCollection from "./users.js";
import type { SecurityRule } from "@rebasepro/types";

/**
 * Everything that hangs off a customer, declared here rather than in
 * `customers.ts`.
 *
 * The admin shows these under the record itself, which is where the questions
 * get asked: what did this person buy, when did they change tier and why, which
 * pass are they carrying, what did we last try to sync. Each child already
 * points back with a `belongsTo`; these are the same links read from this side.
 *
 * They live in this file because declaring them on the collection would mean
 * `customers.ts` importing the four modules that already import it. The runtime
 * cycle is harmless — the targets are thunks — but TypeScript has to resolve
 * each collection's type to resolve the others, gives up, and types them all as
 * `any`. Here, every collection is already imported and fully typed.
 *
 * `foreignKeyOnTarget` is spelled out rather than left to the default:
 * `sync_logs` genuinely differs, and for the other three a wrong guess would
 * either fail loudly or — worse — read the whole table under every customer.
 */
customersCollection.relations = [
    {
        kind: "hasMany",
        relationName: "transactions",
        target: () => transactionsCollection,
        foreignKeyOnTarget: "customer_id"
    },
    {
        kind: "hasMany",
        relationName: "tier_changes",
        target: () => tierChangesCollection,
        foreignKeyOnTarget: "customer_id"
    },
    {
        kind: "hasMany",
        relationName: "wallet_passes",
        target: () => walletPassesCollection,
        foreignKeyOnTarget: "customer_id"
    },
    {
        kind: "hasMany",
        relationName: "sync_logs",
        target: () => syncLogsCollection,
        foreignKeyOnTarget: "related_customer_id"
    }
];

export const collections = [
    tiersCollection,
    customersCollection,
    transactionsCollection,
    tierChangesCollection,
    walletPassesCollection,
    walletSettingsCollection,
    walletDevicesCollection,
    syncLogsCollection,
    usersCollection
];

/**
 * Applied to any collection here that declares no `securityRules` of its own:
 * anyone can read, only admins can write.
 *
 * These live next to the collections because `rebase db push` generates the
 * Postgres policies from these files — that is what actually enforces access.
 * A default declared on the server config (where this used to live) never
 * reached the database.
 *
 * Every collection in this directory currently declares its own rules, so this
 * is a floor for anything added later. A collection that declares neither its
 * own rules nor inherits these is locked to admin-only by the generator.
 */
export const defaultSecurityRules: SecurityRule[] = [
    { operation: "select", access: "public" },
    { operations: ["insert", "update", "delete"], roles: ["admin"] }
];
